import React, { useState } from 'react'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  CheckCircle as VerifiedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
} from '@mui/icons-material'
import { Document } from '../../types/api'
import { useAuth } from '../../contexts/AuthContext'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'

interface DocumentListGroupedProps {
  documents: Document[]
  onView: (doc: Document) => void
  onDocumentVerified?: () => void  // Callback pentru refresh după verificare
}

// Mapping categories labels
const CATEGORY_LABELS: Record<string, string> = {
  referral: 'Bilete de Trimitere',
  medical_letter: 'Scrisori Medicale',
  external_result: 'Rezultate Externe',
  external_report: 'Rapoarte Imagistice',
  external_image: 'Imagini Externe',
  consent: 'Consimțăminte',
  invoice: 'Facturi',
  generic: 'Documente Diverse',
  study_attachment: 'Atașamente Studii'
}

const DocumentListGrouped: React.FC<DocumentListGroupedProps> = ({ documents, onView, onDocumentVerified }) => {
  const { user } = useAuth()
  const [verifyDialog, setVerifyDialog] = useState<{ open: boolean; document: Document | null; action: 'approve' | 'reject' | null }>({
    open: false,
    document: null,
    action: null
  })
  const [verifyNotes, setVerifyNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const canVerify = user?.role === 'admin' || user?.role === 'medic'

  const handleVerifyClick = (doc: Document, action: 'approve' | 'reject') => {
    setVerifyDialog({ open: true, document: doc, action })
    setVerifyNotes('')
  }

  const handleVerifySubmit = async () => {
    if (!verifyDialog.document || !verifyDialog.action) return

    setSubmitting(true)
    const statusMap = {
      approve: 'verified',
      reject: 'rejected'
    }

    try {
      console.log('Verifying document:', {
        id: verifyDialog.document.id,
        action: verifyDialog.action,
        url: `/documents/${verifyDialog.document.id}/verify`
      })
      
      await apiClient.patch(`/documents/${verifyDialog.document.id}/verify`, {
        verification_status: statusMap[verifyDialog.action],
        notes: verifyNotes || null
      })
      toast.success(`Document ${verifyDialog.action === 'approve' ? 'validat' : 'respins'} cu succes`)
      setVerifyDialog({ open: false, document: null, action: null })
      if (onDocumentVerified) onDocumentVerified()
    } catch (error: any) {
      console.error('Verify error:', error)
      console.error('Error response:', error.response)
      console.error('Document ID was:', verifyDialog.document.id)
      toast.error(error.response?.data?.detail || 'Eroare la verificare')
    } finally {
      setSubmitting(false)
    }
  }
  
  // Group documents by kind
  const groupedDocs = documents.reduce((acc, doc) => {
    const kind = doc.kind || 'generic'
    if (!acc[kind]) {
      acc[kind] = []
    }
    acc[kind].push(doc)
    return acc
  }, {} as Record<string, Document[]>)

  // Sort categories order (optional priority)
  const sortedCategories = Object.keys(groupedDocs).sort()

  const getIcon = (mime: string) => {
    if (mime.includes('pdf')) return <PdfIcon color="error" />
    if (mime.includes('image')) return <ImageIcon color="primary" />
    return <FileIcon color="action" />
  }

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'verified':
        return <Tooltip title="Validat"><VerifiedIcon color="success" fontSize="small" /></Tooltip>
      case 'rejected':
        return <Tooltip title="Respins"><RejectedIcon color="error" fontSize="small" /></Tooltip>
      default:
        return <Tooltip title="În așteptare"><PendingIcon color="warning" fontSize="small" /></Tooltip>
    }
  }

  if (documents.length === 0) {
    return (
      <Box p={2} textAlign="center" color="text.secondary">
        <Typography>Nu există documente încărcate.</Typography>
      </Box>
    )
  }

  return (
    <Box>
      {sortedCategories.map((category) => (
        <Accordion key={category} defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'action.hover' }}>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Typography variant="subtitle2" fontWeight="bold">
                {CATEGORY_LABELS[category] || category.toUpperCase()}
              </Typography>
              <Chip 
                label={groupedDocs[category].length} 
                size="small" 
                color="primary" 
                variant="outlined" 
                sx={{ height: 20, fontSize: '0.7rem' }} 
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List disablePadding>
              {groupedDocs[category].map((doc) => (
                <ListItem
                  key={doc.id}
                  divider
                  sx={{ 
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: 'background.default' }
                  }}
                  secondaryAction={
                    <Box display="flex" gap={0.5} alignItems="center">
                      {/* View button */}
                      <IconButton edge="end" onClick={() => onView(doc)} size="small">
                        <ViewIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getIcon(doc.mime)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" noWrap title={doc.file_name} sx={{ maxWidth: 300 }}>
                          {doc.file_name}
                        </Typography>
                        {getStatusIcon(doc.verification_status)}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {doc.document_date ? `Data: ${doc.document_date} • ` : ''}
                        {doc.source_institution ? `${doc.source_institution} • ` : ''}
                        {(doc.size_bytes / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Verification Dialog */}
      <Dialog open={verifyDialog.open} onClose={!submitting ? () => setVerifyDialog({ open: false, document: null, action: null }) : undefined} maxWidth="xs" fullWidth>
        <DialogTitle>
          {verifyDialog.action === 'approve' ? 'Validare Document' : 'Respingere Document'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Document: <strong>{verifyDialog.document?.file_name}</strong>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notițe (opțional)"
            value={verifyNotes}
            onChange={(e) => setVerifyNotes(e.target.value)}
            disabled={submitting}
            sx={{ mt: 2 }}
            placeholder="Adaugă observații despre verificare..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, document: null, action: null })} disabled={submitting}>
            Anulează
          </Button>
          <Button
            onClick={handleVerifySubmit}
            variant="contained"
            color={verifyDialog.action === 'approve' ? 'success' : 'error'}
            disabled={submitting}
          >
            {verifyDialog.action === 'approve' ? 'Validează' : 'Respinge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DocumentListGrouped

